import type { TableDataRow } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { readThrough } from '@/lib/offlineCache';
import { formatValue } from '@/lib/format';
import {
    friendlyError,
    isNotFoundError,
    isPermissionDeniedError,
    logSwallowed,
} from '@/lib/errors';
import {
    listEcgStudies,
    resolveEcgPatientColumn,
    type EcgStudyListItem,
} from '@/lib/ecgStudies';
import {
    loadAppointments,
    loadAppointmentEnumLabels,
    type CabinetAppointment,
} from '@/lib/cabinet/appointments';
import {
    loadAssignments,
    loadMethods,
    type PatientResult,
    type PsyAssignment,
    type PsyMethod,
} from '@/lib/psyWorkspace';
import { loadPatientRecords, type PatientRecordsData } from './patientCardRecordsData';
import { loadAmbulanceHistory, type AmbulanceHistory } from './patientAmbulanceData';

export const PATIENT_CATALOG = 'medic_patients';
export const PATIENT_GENDER_ENUM = 'medic_gender';
export const PSY_CONCLUSIONS = 'psy_conclusions';

export type SourceState = 'ready' | 'unavailable' | 'denied' | 'error';

export interface PatientBasic {
    id: string;
    code: string;
    name: string;
    birthDate: string;
    genderCode: string;
    genderLabel: string;
    phone: string;
    email: string;
    address: string;
    snils: string;
    comment: string;
    linkedUserId: string;
    /** Явная связь «лечащий врач» (medic_patients.attending_doctor_id). */
    attendingDoctorId: string;
    attendingDoctorName: string;
}

export interface EcgHistory {
    state: SourceState;
    studies: EcgStudyListItem[];
    error: string;
    note: string;
}

export interface PsyConclusion {
    id: string;
    date: string;
    text: string;
    recommendations: string;
    icdCode: string;
    sessionId: string;
}

export interface PsyHistory {
    state: SourceState;
    methods: PsyMethod[];
    assignments: PsyAssignment[];
    assignmentsState: SourceState;
    assignmentsError: string;
    results: PatientResult[];
    resultsState: SourceState;
    resultsError: string;
    conclusions: PsyConclusion[];
    conclusionsState: SourceState;
    conclusionsError: string;
    note: string;
}

export interface AppointmentsHistory {
    state: SourceState;
    items: CabinetAppointment[];
    statusLabels: Record<string, string>;
    typeLabels: Record<string, string>;
    error: string;
    note: string;
}

export interface PatientCardData {
    patient: PatientBasic;
    ecg: EcgHistory;
    psy: PsyHistory;
    appointments: AppointmentsHistory;
    records: PatientRecordsData;
    /** История вызовов скорой (документ ambulance_calls вертикали «Скорая»). */
    ambulance: AmbulanceHistory;
}

export interface DateRange {
    from: string;
    to: string;
}

interface Row extends Record<string, string> {
    id: string;
}

interface PsyResultsLoad {
    items: PatientResult[];
    state: SourceState;
    error: string;
}

const inflight = new Map<string, Promise<PatientCardData>>();

function slug(): string {
    return useCenterStore.getState().baseSlug || 'local';
}

function cellsOf(row: TableDataRow): Record<string, string> {
    const out: Record<string, string> = {};
    for (const value of row.values ?? []) {
        out[String(value.column)] = value.value === null || value.value === undefined
            ? ''
            : String(value.value);
    }
    return out;
}

function rowsOf(rows: TableDataRow[]): Row[] {
    return rows.map((row) => ({ id: String(row.id || ''), ...cellsOf(row) }));
}

function isTrue(value: string): boolean {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'да';
}

async function loadGenderLabels(): Promise<Record<string, string>> {
    try {
        const response = await getSDK().metadata.getEnum({ code: PATIENT_GENDER_ENUM });
        const labels: Record<string, string> = {};
        for (const value of response.values || []) {
            labels[String(value.value)] = String(value.synonym || value.name || value.value);
        }
        return labels;
    } catch (e) {
        if (!isNotFoundError(e)) logSwallowed(e, 'Карточка пациента: перечисление пола');
        return {};
    }
}

async function fetchPatientBasic(patientId: string): Promise<PatientBasic> {
    const [response, genderLabels] = await Promise.all([
        getSDK().metadata.listTableData({
            schema: 'public',
            table: PATIENT_CATALOG,
            pageSize: 1,
            page: 0,
            filters: [{ column: 'id', value: patientId }],
        }),
        loadGenderLabels(),
    ]);
    const row = rowsOf(response.rows)[0];
    if (!row) throw new Error('Пациент не найден или недоступен');
    const genderCode = row.gender || '';
    return {
        id: row.id,
        code: row.code || '',
        name: row.name || '',
        birthDate: row.birth_date || '',
        genderCode,
        genderLabel: genderLabels[genderCode] || row.gender_presentation || genderCode,
        phone: row.phone || '',
        email: row.email || '',
        address: row.address || '',
        snils: row.snils || '',
        comment: row.comment || '',
        linkedUserId: row.user_id || '',
        attendingDoctorId: row.attending_doctor_id || '',
        attendingDoctorName: row.attending_doctor_id_presentation || '',
    };
}

async function loadPatientBasic(patientId: string): Promise<PatientBasic> {
    try {
        const result = await readThrough(
            `patient-card:basic:v2:${slug()}:${patientId}`,
            () => fetchPatientBasic(patientId),
        );
        return result.data;
    } catch (e) {
        if (isNotFoundError(e)) {
            throw new Error('Карточка пациента не найдена или удалена.');
        }
        if (isPermissionDeniedError(e)) {
            throw new Error('Нет прав на чтение карточки пациента');
        }
        throw e;
    }
}

async function fetchEcgHistory(patient: PatientBasic): Promise<EcgHistory> {
    const patientColumn = await resolveEcgPatientColumn();
    if (patientColumn) {
        const result = await readThrough(
            `patient-card:ecg-patient:${slug()}:${patient.id}`,
            () => listEcgStudies({
                patient: { id: patient.id, source: 'patient' },
                limit: 500,
            }),
        );
        return { state: 'ready', studies: result.data, error: '', note: '' };
    }
    if (patient.linkedUserId) {
        const result = await readThrough(
            `patient-card:ecg-owner:${slug()}:${patient.linkedUserId}`,
            () => listEcgStudies({ ownerId: patient.linkedUserId, limit: 500 }),
        );
        return {
            state: 'ready',
            studies: result.data,
            error: '',
            note: '',
        };
    }
    return {
        state: 'unavailable',
        studies: [],
        error: '',
        note: 'История ЭКГ для этого пациента пока не подключена. Обратитесь к администратору базы.',
    };
}

async function loadEcgHistory(patient: PatientBasic): Promise<EcgHistory> {
    try {
        return await fetchEcgHistory(patient);
    } catch (e) {
        if (isPermissionDeniedError(e)) {
            return { state: 'denied', studies: [], error: 'Нет прав на чтение исследований ЭКГ', note: '' };
        }
        if (isNotFoundError(e)) {
            return {
                state: 'unavailable',
                studies: [],
                error: '',
                note: 'ЭКГ в этой базе не ведётся.',
            };
        }
        return { state: 'error', studies: [], error: friendlyError(e), note: '' };
    }
}

async function fetchPsyConclusions(patientId: string): Promise<PsyConclusion[]> {
    const response = await getSDK().metadata.listTableData({
        schema: 'public',
        table: PSY_CONCLUSIONS,
        pageSize: 500,
        page: 0,
        filters: [{ column: 'patient_id', value: patientId }],
    });
    return rowsOf(response.rows)
        .map((row) => ({
            id: row.id,
            date: row.conclusion_date || '',
            text: row.conclusion_text || '',
            recommendations: row.recommendations || '',
            icdCode: row.icd_code || '',
            sessionId: row.session_id || '',
        }))
        .filter((item) => item.id !== '')
        .sort((a, b) => b.date.localeCompare(a.date));
}

async function loadPsyConclusions(patientId: string): Promise<PsyConclusion[]> {
    const result = await readThrough(
        `patient-card:psy-conclusions:${slug()}:${patientId}`,
        () => fetchPsyConclusions(patientId),
    );
    return result.data;
}

async function fetchResultsForMethods(methods: PsyMethod[], patientId: string): Promise<PsyResultsLoad> {
    const items: PatientResult[] = [];
    for (const method of methods) {
        try {
            const response = await getSDK().metadata.listTableData({
                schema: 'public',
                table: method.code,
                pageSize: 100,
                page: 0,
                filters: [{ column: 'subject_id', value: patientId }],
            });
            for (const row of rowsOf(response.rows)) {
                items.push({
                    id: row.id,
                    methodCode: method.code,
                    methodName: method.synonym,
                    createdAt: row.created_at || '',
                    score: row.score || '',
                    passed: isTrue(row.passed || ''),
                    conclusion: row.conclusion || '',
                });
            }
        } catch (e) {
            if (isPermissionDeniedError(e)) {
                return { items, state: 'denied', error: 'Нет прав на чтение результатов тестов' };
            }
            if (isNotFoundError(e)) {
                logSwallowed(e, `Карточка пациента: протоколы ${method.code}`);
                continue;
            }
            return { items, state: 'error', error: friendlyError(e) };
        }
    }
    items.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return { items, state: 'ready', error: '' };
}

async function loadPsyResults(methods: PsyMethod[], patientId: string): Promise<PsyResultsLoad> {
    if (methods.length === 0) return { items: [], state: 'ready', error: '' };
    const methodKey = methods.map((method) => method.code).sort().join('|');
    const result = await readThrough(
        `patient-card:psy-results:${slug()}:${patientId}:${methodKey}`,
        () => fetchResultsForMethods(methods, patientId),
    );
    return result.data;
}

function errorState(e: unknown): { state: SourceState; error: string } {
    if (isPermissionDeniedError(e)) return { state: 'denied', error: 'Нет прав на чтение' };
    if (isNotFoundError(e)) return { state: 'unavailable', error: '' };
    return { state: 'error', error: friendlyError(e) };
}

async function loadPsyHistory(patientId: string): Promise<PsyHistory> {
    const history: PsyHistory = {
        state: 'ready',
        methods: [],
        assignments: [],
        assignmentsState: 'ready',
        assignmentsError: '',
        results: [],
        resultsState: 'ready',
        resultsError: '',
        conclusions: [],
        conclusionsState: 'ready',
        conclusionsError: '',
        note: '',
    };

    const [methodsOutcome, assignmentsOutcome, conclusionsOutcome] = await Promise.allSettled([
        loadMethods(),
        loadAssignments(patientId),
        loadPsyConclusions(patientId),
    ]);

    if (methodsOutcome.status === 'fulfilled') {
        history.methods = methodsOutcome.value;
    } else {
        const mapped = errorState(methodsOutcome.reason);
        history.resultsState = mapped.state;
        history.resultsError = mapped.error;
    }

    if (assignmentsOutcome.status === 'fulfilled') {
        history.assignments = assignmentsOutcome.value;
    } else {
        const mapped = errorState(assignmentsOutcome.reason);
        history.assignmentsState = mapped.state;
        history.assignmentsError = mapped.error;
    }

    if (conclusionsOutcome.status === 'fulfilled') {
        history.conclusions = conclusionsOutcome.value;
    } else {
        const mapped = errorState(conclusionsOutcome.reason);
        history.conclusionsState = mapped.state;
        history.conclusionsError = mapped.error;
    }

    if (methodsOutcome.status === 'fulfilled') {
        try {
            const results = await loadPsyResults(history.methods, patientId);
            history.results = results.items;
            history.resultsState = results.state;
            history.resultsError = results.error;
        } catch (e) {
            const mapped = errorState(e);
            history.resultsState = mapped.state;
            history.resultsError = mapped.error;
        }
    }

    const hasAnyData = history.assignments.length > 0
        || history.results.length > 0
        || history.conclusions.length > 0;
    const allMissing = history.assignmentsState === 'unavailable'
        && history.conclusionsState === 'unavailable'
        && history.results.length === 0
        && history.methods.length === 0;
    const anyDenied = history.assignmentsState === 'denied'
        || history.conclusionsState === 'denied'
        || history.resultsState === 'denied';
    const firstError = [history.assignmentsError, history.resultsError, history.conclusionsError]
        .find((message) => !!message) || '';

    if (!hasAnyData) {
        if (allMissing) {
            history.state = 'unavailable';
            history.note = 'Психологические данные в этой базе не ведутся.';
        } else if (anyDenied) {
            history.state = 'denied';
            history.note = 'Нет доступа к психологическим данным пациента.';
        } else if (firstError) {
            history.state = 'error';
            history.note = firstError;
        }
    }

    return history;
}

async function loadAppointmentsHistory(patientId: string): Promise<AppointmentsHistory> {
    try {
        const items = await loadAppointments(patientId);
        let statusLabels: Record<string, string> = {};
        let typeLabels: Record<string, string> = {};
        try {
            const labels = await loadAppointmentEnumLabels();
            statusLabels = labels.status;
            typeLabels = labels.type;
        } catch (e) {
            logSwallowed(e, 'Карточка пациента: подписи приёмов');
        }
        items.sort((a, b) => {
            const aUpcoming = isUpcoming(a.appointmentAt);
            const bUpcoming = isUpcoming(b.appointmentAt);
            if (aUpcoming && bUpcoming) return dateTimeKey(a.appointmentAt).localeCompare(dateTimeKey(b.appointmentAt));
            if (aUpcoming) return -1;
            if (bUpcoming) return 1;
            return dateTimeKey(b.appointmentAt).localeCompare(dateTimeKey(a.appointmentAt));
        });
        return { state: 'ready', items, statusLabels, typeLabels, error: '', note: '' };
    } catch (e) {
        const mapped = errorState(e);
        return {
            state: mapped.state,
            items: [],
            statusLabels: {},
            typeLabels: {},
            error: mapped.error,
            note: mapped.state === 'unavailable'
                ? 'Приёмы в этой базе не ведутся.'
                : '',
        };
    }
}

async function loadAll(patientId: string): Promise<PatientCardData> {
    const patient = await loadPatientBasic(patientId);
    const [ecg, psy, appointments, records, ambulance] = await Promise.all([
        loadEcgHistory(patient),
        loadPsyHistory(patientId),
        loadAppointmentsHistory(patientId),
        loadPatientRecords(patientId),
        loadAmbulanceHistory(patientId),
    ]);
    return { patient, ecg, psy, appointments, records, ambulance };
}

export function loadPatientCardData(patientId: string, force = false): Promise<PatientCardData> {
    if (!patientId) return Promise.reject(new Error('Не указан пациент'));
    if (!force) {
        const existing = inflight.get(patientId);
        if (existing) return existing;
    }
    let tracked: Promise<PatientCardData>;
    tracked = loadAll(patientId).finally(() => {
        if (inflight.get(patientId) === tracked) inflight.delete(patientId);
    });
    inflight.set(patientId, tracked);
    return tracked;
}

export function emptyRange(): DateRange {
    return { from: '', to: '' };
}

export function hasRange(range: DateRange): boolean {
    return !!range.from || !!range.to;
}

export function rangeInvalid(range: DateRange): boolean {
    return !!range.from && !!range.to && range.from > range.to;
}

export function dateKey(value: string): string {
    const formatted = formatValue(value, 'date:yyyy-MM-dd', 'timestamp');
    if (/^\d{4}-\d{2}-\d{2}$/.test(formatted)) return formatted;
    return String(value || '').slice(0, 10);
}

export function dateTimeKey(value: string): string {
    const formatted = formatValue(value, 'date:yyyy-MM-dd HH:mm:ss', 'timestamp');
    return formatted || String(value || '');
}

export function inRange(value: string, range: DateRange): boolean {
    if (!hasRange(range)) return true;
    const key = dateKey(value);
    if (!key) return false;
    if (range.from && key < range.from) return false;
    if (range.to && key > range.to) return false;
    return true;
}

export function filterByRange<T>(items: T[], getValue: (item: T) => string, range: DateRange): T[] {
    if (rangeInvalid(range)) return [];
    if (!hasRange(range)) return items;
    return items.filter((item) => inRange(getValue(item), range));
}

export function isUpcoming(value: string): boolean {
    const key = dateTimeKey(value);
    return !!key && key >= dateTimeKey(new Date().toISOString());
}
